import React, { useState } from 'react';
import {
  renameCompany,
  deleteCompany,
  saveCustomCompanyLocal,
  getCompanyLogoLocal,
  uploadProductImage,
} from '../api/productApi';
import { resolveImageUrl } from '../utils/image';
import './AdminDashboard.css';

interface ManageCompaniesModalProps {
  isOpen: boolean;
  companies: string[];
  companyLogos?: Record<string, string>;
  onClose: () => void;
  onCompaniesUpdated: (
    updatedList: string[],
    renamed?: { oldName: string; newName: string; logoUrl?: string },
    removed?: string
  ) => void;
}

export default function ManageCompaniesModal({
  isOpen,
  companies,
  companyLogos = {},
  onClose,
  onCompaniesUpdated,
}: ManageCompaniesModalProps) {
  const [newCompany, setNewCompany] = useState('');
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [uploadingNewLogo, setUploadingNewLogo] = useState(false);

  const [editingCompany, setEditingCompany] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editLogoUrl, setEditLogoUrl] = useState('');
  const [uploadingEditLogo, setUploadingEditLogo] = useState(false);

  const [confirmRemoveCompany, setConfirmRemoveCompany] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!isOpen) return null;

  const getLogoFor = (compName: string): string => {
    const key = compName.trim().toLowerCase();
    return companyLogos[key] || companyLogos[compName] || getCompanyLogoLocal(compName) || '';
  };

  const handleUploadNewLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingNewLogo(true);
    try {
      const res = await uploadProductImage(file);
      if (res?.path) {
        setNewLogoUrl(res.path);
      }
    } catch {
      alert('Failed to upload logo image. Please try again or paste an image URL.');
    } finally {
      setUploadingNewLogo(false);
      e.target.value = '';
    }
  };

  const handleUploadEditLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEditLogo(true);
    try {
      const res = await uploadProductImage(file);
      if (res?.path) {
        setEditLogoUrl(res.path);
      }
    } catch {
      alert('Failed to upload logo image. Please try again or paste an image URL.');
    } finally {
      setUploadingEditLogo(false);
      e.target.value = '';
    }
  };

  const handleAdd = () => {
    const trimmed = newCompany.trim();
    if (!trimmed) return;
    const trimmedLogo = newLogoUrl.trim();
    saveCustomCompanyLocal(trimmed, trimmedLogo || undefined);
    const next = Array.from(new Set([...companies, trimmed])).sort((a, b) => a.localeCompare(b));
    onCompaniesUpdated(next, { oldName: trimmed, newName: trimmed, logoUrl: trimmedLogo });
    setNewCompany('');
    setNewLogoUrl('');
  };

  const startEdit = (comp: string) => {
    setEditingCompany(comp);
    setEditValue(comp);
    setEditLogoUrl(getLogoFor(comp));
    setConfirmRemoveCompany(null);
  };

  const handleSaveRename = async (oldName: string) => {
    const trimmed = editValue.trim();
    const trimmedLogo = editLogoUrl.trim();
    if (!trimmed) {
      setEditingCompany(null);
      return;
    }
    setBusy(true);
    try {
      await renameCompany(oldName, trimmed, trimmedLogo, true);
      const next = Array.from(
        new Set(
          companies.map(c => (c.toLowerCase() === oldName.toLowerCase() ? trimmed : c))
        )
      ).sort((a, b) => a.localeCompare(b));
      onCompaniesUpdated(next, { oldName, newName: trimmed, logoUrl: trimmedLogo });
      setEditingCompany(null);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmRemove = async (comp: string) => {
    setBusy(true);
    try {
      await deleteCompany(comp);
      const next = companies.filter(c => c.toLowerCase() !== comp.toLowerCase());
      onCompaniesUpdated(next, undefined, comp);
      setConfirmRemoveCompany(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ad-modal-backdrop" onClick={onClose}>
      <div className="ad-modal-card" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="ad-modal-header">
          <div className="ad-modal-title-group">
            <span className="ad-modal-tag">BRAND DIRECTORY</span>
            <h2>Manage Companies & Brand Logos</h2>
          </div>
          <button type="button" className="ad-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="ad-modal-body">
          {/* Add New Company + Logo Box */}
          <div className="ad-company-manage-add-card">
            <div className="ad-company-manage-add">
              <input
                type="text"
                value={newCompany}
                onChange={e => setNewCompany(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                className="ad-input"
                placeholder="New company name (e.g. Standard, Sony, Cock Brand)..."
              />
              <button
                type="button"
                className="ad-btn-add-url"
                onClick={handleAdd}
                disabled={!newCompany.trim() || busy || uploadingNewLogo}
              >
                + Add Company
              </button>
            </div>

            <div className="ad-company-logo-controls">
              <div className="ad-company-logo-preview-mini">
                {newLogoUrl.trim() ? (
                  <img
                    src={resolveImageUrl(newLogoUrl)}
                    alt="Logo preview"
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <span>🏢</span>
                )}
              </div>
              <input
                type="text"
                value={newLogoUrl}
                onChange={e => setNewLogoUrl(e.target.value)}
                className="ad-input"
                placeholder="Paste company logo image URL (optional)..."
              />
              <label className="ad-company-upload-label">
                {uploadingNewLogo ? 'Uploading...' : '📤 Upload Logo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadNewLogoFile}
                  disabled={uploadingNewLogo || busy}
                  style={{ display: 'none' }}
                />
              </label>
              {newLogoUrl && (
                <button
                  type="button"
                  className="ad-btn-action close"
                  onClick={() => setNewLogoUrl('')}
                  title="Clear logo"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* List of Companies */}
          <div className="ad-company-manage-list">
            {companies.length === 0 ? (
              <div className="ad-company-manage-empty">
                No companies in the list yet. Add one above!
              </div>
            ) : (
              companies.map((comp) => {
                const isEditing = editingCompany === comp;
                const isConfirmingRemove = confirmRemoveCompany === comp;
                const compLogo = getLogoFor(comp);

                return (
                  <div key={comp} className="ad-company-manage-item">
                    {isEditing ? (
                      <div className="ad-company-manage-edit-block">
                        <div className="ad-company-manage-edit-row">
                          <input
                            type="text"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveRename(comp);
                              } else if (e.key === 'Escape') {
                                setEditingCompany(null);
                              }
                            }}
                            className="ad-input"
                            placeholder="Company name..."
                            autoFocus
                          />
                          <button
                            type="button"
                            className="ad-btn-action complete"
                            onClick={() => handleSaveRename(comp)}
                            disabled={busy || uploadingEditLogo}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="ad-btn-action close"
                            onClick={() => setEditingCompany(null)}
                            disabled={busy}
                          >
                            Cancel
                          </button>
                        </div>

                        <div className="ad-company-logo-controls">
                          <div className="ad-company-logo-preview-mini">
                            {editLogoUrl.trim() ? (
                              <img
                                src={resolveImageUrl(editLogoUrl)}
                                alt={editValue}
                                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <span>🏢</span>
                            )}
                          </div>
                          <input
                            type="text"
                            value={editLogoUrl}
                            onChange={e => setEditLogoUrl(e.target.value)}
                            className="ad-input"
                            placeholder="Company logo URL..."
                          />
                          <label className="ad-company-upload-label">
                            {uploadingEditLogo ? 'Uploading...' : '📤 Upload Logo'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleUploadEditLogoFile}
                              disabled={uploadingEditLogo || busy}
                              style={{ display: 'none' }}
                            />
                          </label>
                          {editLogoUrl && (
                            <button
                              type="button"
                              className="ad-btn-action close"
                              onClick={() => setEditLogoUrl('')}
                              title="Remove logo"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    ) : isConfirmingRemove ? (
                      <div className="ad-company-manage-confirm-row">
                        <span className="ad-company-confirm-text">
                          Remove <strong>"{comp}"</strong> from list & products?
                        </span>
                        <div className="ad-actions-cell">
                          <button
                            type="button"
                            className="ad-btn-action delete"
                            onClick={() => handleConfirmRemove(comp)}
                            disabled={busy}
                          >
                            Yes, Remove
                          </button>
                          <button
                            type="button"
                            className="ad-btn-action close"
                            onClick={() => setConfirmRemoveCompany(null)}
                            disabled={busy}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="ad-company-manage-name">
                          <div className="ad-company-logo-preview-mini">
                            {compLogo ? (
                              <img
                                src={resolveImageUrl(compLogo)}
                                alt={comp}
                                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <span>🏢</span>
                            )}
                          </div>
                          <div>
                            <strong>{comp}</strong>
                            {compLogo && (
                              <div className="ad-company-logo-subtext">Logo attached</div>
                            )}
                          </div>
                        </div>
                        <div className="ad-actions-cell">
                          <button
                            type="button"
                            className="ad-btn-action edit"
                            onClick={() => startEdit(comp)}
                            disabled={busy}
                          >
                            Modify
                          </button>
                          <button
                            type="button"
                            className="ad-btn-action delete"
                            onClick={() => {
                              setConfirmRemoveCompany(comp);
                              setEditingCompany(null);
                            }}
                            disabled={busy}
                          >
                            Remove
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="ad-modal-footer">
          <button type="button" className="ad-btn-action close" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
